/* eslint-disable no-param-reassign */

import { getParent, types, getRoot } from "mobx-state-tree";
import { last } from "lodash";
import { notification } from "antd";
import React from "react";

import MessageModel from "data/models/message";
// import SessionModel from "../session";
import ContactModel from "data/models/contact";
import ApiService from "services/api";
import { MessageTwoTone } from "@ant-design/icons";
import TagModel from "data/models/tag";

const BaseChat = types
  .model("BaseChatModel", {
    id: types.identifier,
    messages: types.array(MessageModel),
    isOnline: types.maybeNull(types.boolean),
    consultant: types.model({
      id: types.number,
      name: types.string,
      avatar: types.string,
    }),
    contact: ContactModel,
    isTrial: true,
    type: types.maybeNull(types.string),
    warning: types.maybeNull(types.string),
    unreadMessagesCount: 0,
    isTyping: false,
    paidMinutes: 0,
    lastContactMessageAt: types.maybeNull(types.string),
    lastUserMessageAt: types.maybeNull(types.string),
    tempMessage: "",
    tag: types.safeReference(TagModel),
  })
  .actions((self) => ({
    afterCreate() {
      self.setTag(self.id);
    },

    setTag(tag) {
      self.tag = tag;
      return self;
    },

    changeTempMessage(message) {
      self.tempMessage = message;
      return self;
    },

    addNewMessage(message) {
      if (self.lastMessage.id === message.id) return;
      self.addMessage(message);
      const chats = getParent(self, 3); // link to repository
      if (chats.activeChat !== self) {
        if (message.fromContact) {
          notification.info({
            description: message.text,
            message: self.contact.nameOrId,
            icon: <MessageTwoTone />,
          });
        }
        self.incrementUnreadMessagesCount();
      }
    },

    addMessage(message) {
      self.messages.push(message);
      if (message.fromUser) self.lastUserMessageAt = message.createdAt;
      if (message.fromContact) self.lastContactMessageAt = message.createdAt;
      return self;
    },

    send(data) {
      const formatedData = { ...data, source: self.type };
      ApiService.sendMessage(self.id, formatedData).catch(() => {
        notification["error"]({
          message: `Message has not been send`,
        });
      });
    },

    addEarlierMessages(messages) {
      self.messages.splice(0, 0, ...messages);
      return self;
    },

    addTrialMinutes(minutes = 3) {
      ApiService.addTrialMinutes(self.id, minutes)
        .then(() => {
          notification["success"]({
            message: `Success ${minutes} trial minutes add.`,
          });
        })
        .catch(() => {
          notification["error"]({
            message: `Fail ${minutes} trial minutes add.`,
          });
        });
    },

    setInitialMessages(messages) {
      self.messages = messages;
      return self;
    },

    markAllUnreadMessages() {
      self.unreadMessagesCount = 0;
      return self;
    },

    incrementUnreadMessagesCount() {
      self.unreadMessagesCount += 1;
      return self;
    },

    setTypingStatus(status) {
      self.isTyping = status;
      return self;
    },

    getEarlierMessages() {
      return ApiService.getMessages(self.id, self.messages.length, 50)
        .then(({ data }) => self.addEarlierMessages(data.reverse()))
        .catch(() => {
          notification["error"]({
            message: "Messages has not been loaded",
          });
        });
    },

    resetMessages() {
      self.messages = self.messages.slice(-1);
      return self;
    },

    async updateTag(value) {
      try {
        if (!value && self.tag) {
          await ApiService.deleteTag(self.tag.id);
          const root = getRoot(self);
          root.tags.remove(self.tag.id);
        } else if (self.tag) {
          const { data } = await ApiService.createOrUpdateTag({
            chatRoomId: self.id,
            tag: value,
          });
          self.tag.update(data);
        } else {
          const { data } = await ApiService.createOrUpdateTag({
            chatRoomId: self.id,
            tag: value,
          });
          const root = getRoot(self);
          root.tags.add(data);
          self.setTag(self.id);
        }
      } catch (error) {
        console.error(error);
        notification.error({ message: "Chat tag can't be updated" });
      }
    },
  }))
  .views((self) => ({
    get lastMessage() {
      return last(self.messages) || {};
    },

    get lastContactMessageAtJSDate() {
      return new Date(self.lastContactMessageAt);
    },

    get lastUserMessageAtJSDate() {
      return new Date(self.lastUserMessageAt);
    },
  }));

export default BaseChat;
